/**
 * UnsavedChangesDialog
 *
 * A confirmation dialog shown when attempting to close a drawer
 * that has unsaved changes.
 *
 * Features:
 * - Appears above all drawers (z-index 350)
 * - Cancel returns to drawer
 * - Discard closes drawer and loses changes
 * - Escape key cancels (doesn't discard)
 * - Focus trapped in dialog
 *
 * @see docs/stories/DRAWER-1.5-handle-unsaved-changes-protection.md
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface UnsavedChangesDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Callback when user clicks Cancel (return to drawer) */
  onCancel: () => void;
  /** Callback when user clicks Discard (close drawer, lose changes) */
  onDiscard: () => void;
  /** Optional title of the drawer with unsaved changes */
  drawerTitle?: string;
}

/**
 * Dialog component for confirming discard of unsaved changes.
 *
 * @example
 * ```tsx
 * <UnsavedChangesDialog
 *   isOpen={showConfirmDialog}
 *   onCancel={() => setShowConfirmDialog(false)}
 *   onDiscard={() => {
 *     closeDrawer({ force: true });
 *     setShowConfirmDialog(false);
 *   }}
 *   drawerTitle="Create Task"
 * />
 * ```
 */
export function UnsavedChangesDialog({
  isOpen,
  onCancel,
  onDiscard,
  drawerTitle,
}: UnsavedChangesDialogProps) {
  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent
        className="z-[350]"
        onEscapeKeyDown={(e) => {
          // Escape cancels (returns to drawer), doesn't discard
          e.preventDefault();
          onCancel();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
          <AlertDialogDescription>
            {drawerTitle
              ? `You have unsaved changes in "${drawerTitle}". Discard them?`
              : "You have unsaved changes. Discard them?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onDiscard}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Discard Changes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
